import { motion } from 'framer-motion';
import { Users, Lightbulb, Heart, Zap } from 'lucide-react';
import { useT } from '@/lib/i18n';

export function AboutPreview() {
  const t = useT();
  const values = [
    {
      icon: Users,
      title: t('Partnerschaftlich', 'Partnership-driven'),
      description: t(
        'Wir setzen auf offene und faire Zusammenarbeit auf Augenhöhe. Ihr Erfolg ist unser Antrieb, und gemeinsam finden wir die beste Lösung für Ihr Projekt.',
        'We work openly and fairly, eye to eye. Your success drives us, and together we find the best solution for your project.',
      ),
    },
    {
      icon: Lightbulb,
      title: t('Innovativ', 'Innovative'),
      description: t(
        'Wir investieren kontinuierlich in modernste CNC-Technologie und optimieren unsere Prozesse fortlaufend, um Ihnen stets die beste Qualität und Effizienz zu bieten.',
        'We invest continuously in state-of-the-art CNC technology and refine our processes to deliver the best quality and efficiency.',
      ),
    },
    {
      icon: Heart,
      title: t('Nachhaltig', 'Sustainable'),
      description: t(
        'Unsere Produktion läuft mit 100% Ökostrom von Lichtblick. Regionale Kunden beliefern wir per Lastenrad und als Mitglied im Bundesverband Nachhaltige Wirtschaft setzen wir uns aktiv für eine grünere Industrie ein.',
        'Our production runs on 100 % renewable electricity. Regional customers are supplied by cargo bike, and as a member of the German Federal Association for Sustainable Economy we actively promote a greener industry.',
      ),
    },
    {
      icon: Zap,
      title: t('Flexibel', 'Flexible'),
      description: t(
        'Ob Einzelteile, Kleinserien oder kurzfristige Änderungen: Wir reagieren schnell und unkompliziert, damit Ihre Projekte termingerecht realisiert werden.',
        'Single parts, small series or short-notice changes — we react fast and pragmatically so your projects ship on time.',
      ),
    },
  ];
  const stats = [
    { value: '1995', label: t('Gegründet', 'Founded') },
    { value: '11', label: t('Maschinen', 'Machines') },
    { value: '100%', label: 'Made in Germany' },
  ];
  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 steel-texture" />
      
      <div className="container mx-auto px-4 sm:px-6 relative">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <motion.span
              className="text-primary font-mono text-sm uppercase tracking-wider"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {t('Seit 1995 in Darmstadt', 'In Darmstadt since 1995')}
            </motion.span>
            
            <motion.h2
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold leading-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {t('Tradition trifft ', 'Tradition meets ')}
              <span className="text-primary">{t('Innovation', 'Innovation')}</span>
            </motion.h2>
            
            <motion.p
              className="mt-6 text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {t(
                'Die FERNAU Präzisionstechnik GmbH mit Sitz in Darmstadt blickt auf eine langjährige Erfahrung im Bereich der CNC-Zerspanung zurück. Als zuverlässiger und kompetenter Partner unterstützen wir unsere Kunden bei der Fertigung hochwertiger Bauteile.',
                'FERNAU Präzisionstechnik GmbH, based in Darmstadt, Germany, has decades of experience in CNC machining. As a reliable and competent partner, we support our customers in manufacturing high-quality components.',
              )}
            </motion.p>
            
            <motion.p
              className="mt-4 text-lg text-muted-foreground leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {t(
                'Dabei legen wir großen Wert auf die kontinuierliche Optimierung unserer Produktionsprozesse, um höchste Qualität und Effizienz sicherzustellen.',
                'We place great value on continuously optimizing our production processes to ensure the highest quality and efficiency.',
              )}
            </motion.p>

            {/* Stats */}
            <motion.div
              className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl font-bold font-mono text-primary">{stat.value}</div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right - Values Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="card-industrial p-6"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg">{value.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
